import { onBeforeUnmount, readonly, ref, watch, type Ref } from 'vue'

const NAV_GAP = 12
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'pointerdown'] as const

// Tracks which of `ids` (DOM ids) sits under the sticky nav inside `root`, the
// page's scroll container, and scrolls there on demand.
export function useScrollSpy(root: Ref<HTMLElement | null>, ids: string[], nav: Ref<HTMLElement | null>) {
  const active = ref(ids[0])
  const pinned = ref<string | null>(null)
  let frame = 0

  // Where the sticky nav's bottom sits.
  function navBottom(el: HTMLElement): number {
    const n = nav.value
    if (!n) return 0
    const top = parseFloat(getComputedStyle(n).top) || 0
    return parseFloat(getComputedStyle(el).paddingTop) + top + n.offsetHeight + NAV_GAP
  }

  function update(): void {
    frame = 0
    const el = root.value
    if (!el || pinned.value) return
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 2) {
      active.value = ids[ids.length - 1]
      return
    }
    const line = el.getBoundingClientRect().top + navBottom(el) + 1
    let current = ids[0]
    for (const id of ids) {
      const section = document.getElementById(id)
      if (section && section.getBoundingClientRect().top <= line) current = id
    }
    active.value = current
  }

  function onScroll(): void {
    if (!frame) frame = requestAnimationFrame(update)
  }

  function onUserScroll(): void {
    pinned.value = null
  }

  function scrollTo(id: string, opts: { smooth?: boolean } = {}): void {
    const el = root.value
    const section = document.getElementById(id)
    if (!el || !section) return
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const smooth = opts.smooth !== false && !reduce
    // scrollIntoView would also scroll the overflow:hidden ancestors of `root`.
    const top = section.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop - navBottom(el)
    active.value = id
    pinned.value = id
    el.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' })
  }

  function listen(el: HTMLElement | null | undefined, on: boolean): void {
    if (!el) return
    const fn = on ? el.addEventListener.bind(el) : el.removeEventListener.bind(el)
    fn('scroll', onScroll, { passive: true })
    for (const ev of USER_SCROLL_EVENTS) fn(ev, onUserScroll, { passive: true })
  }

  watch(
    root,
    (el, prev) => {
      listen(prev, false)
      listen(el, true)
    },
    { immediate: true },
  )
  // Keyboard scrolling (PageDown, arrows, space) fires on whatever has focus.
  window.addEventListener('keydown', onUserScroll)
  onBeforeUnmount(() => {
    window.removeEventListener('keydown', onUserScroll)
    listen(root.value, false)
    if (frame) cancelAnimationFrame(frame)
  })

  return { active, pinned: readonly(pinned), scrollTo }
}
